import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../lib/utils.ts"
import { Slot } from "radix-ui"

/*
 * shadcn/ui Badge, restyled to the design language (components.md, Chips and tags):
 * 20 px high, 12 px text at 500, the pill radius, neutral by default (a 6% text-colour tint,
 * fg-muted, so it reads on a panel, a menu or a dialog alike). Status is StatusPill, never a
 * coloured badge.
 */
const badgeVariants = cva(
  "inline-flex h-(--pill-height) w-fit max-w-full shrink-0 items-center justify-center gap-(--pill-gap) overflow-hidden whitespace-nowrap rounded-pill border border-transparent px-(--pill-padding-x) text-caption font-medium [&>svg]:pointer-events-none [&>svg]:size-(--pill-icon-size)",
  {
    variants: {
      variant: {
        default: "bg-fg/6 text-fg-muted",
        secondary: "bg-fg/10 text-fg",
        destructive:
          "border-destructive-line bg-destructive-soft text-destructive-ink",
        outline: "border-border text-fg-muted",
        ghost: "text-fg-muted [a&]:hover:bg-fg/6",
        link: "text-accent-text underline-offset-4 [a&]:hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
